import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Calculator,
  Plus,
  Pencil,
  Trash,
  Check,
  X,
  CurrencyDollar,
  CookingPot
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

export default function PrepCalculator() {
  const [prepItems, setPrepItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [storageAreas, setStorageAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    yield_quantity: "1",
    yield_unit: "EA",
    ingredients: [],
    notes: "",
    track_in_inventory: false,
    inventory_location_ids: [],
    inventory_storage_mappings: [],
    oz_conversion: "",
    pans_to_oz: "",  // How many oz in 1 pan
    allow_in_waste_log: false
  });
  
  const [newIngredient, setNewIngredient] = useState({
    item_type: "product",
    item_id: "",
    quantity: "",
    yield_percent: "100"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prepRes, prodRes, locRes, areasRes] = await Promise.all([
        axios.get(`${API}/prep-items`, { withCredentials: true }),
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/locations`, { withCredentials: true }),
        axios.get(`${API}/storage-areas`, { withCredentials: true })
      ]);
      setPrepItems(prepRes.data);
      setProducts(prodRes.data);
      setLocations(locRes.data);
      setStorageAreas(areasRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getIngredientName = (ing) => {
    if (ing.item_type === "prep") {
      const prep = prepItems.find(p => p.prep_id === ing.item_id);
      return prep?.name || "Unknown Prep";
    }
    const product = products.find(p => p.product_id === ing.item_id);
    return product?.name || "Unknown";
  };

  const getIngredientPrice = (ing) => {
    if (ing.item_type === "prep") {
      const prep = prepItems.find(p => p.prep_id === ing.item_id);
      return prep?.cost_per_unit || 0;
    }
    const product = products.find(p => p.product_id === ing.item_id);
    return product?.price || 0;
  };

  const handleAddIngredient = () => {
    if (!newIngredient.item_id || !newIngredient.quantity) {
      toast.error("Select an ingredient and enter quantity");
      return;
    }

    // Prevent adding self as ingredient when editing
    if (editingItem && newIngredient.item_type === "prep" && newIngredient.item_id === editingItem.prep_id) {
      toast.error("A prep item cannot use itself as an ingredient");
      return;
    }
    
    const name = newIngredient.item_type === "prep"
      ? prepItems.find(p => p.prep_id === newIngredient.item_id)?.name || ""
      : products.find(p => p.product_id === newIngredient.item_id)?.name || "";
    
    const price = getIngredientPrice(newIngredient);
    const yieldPct = parseFloat(newIngredient.yield_percent) || 100;
    
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, {
        item_type: newIngredient.item_type,
        item_id: newIngredient.item_id,
        product_name: name,
        quantity: parseFloat(newIngredient.quantity),
        price_per_unit: price,
        yield_percent: yieldPct
      }]
    }));
    setNewIngredient({ item_type: "product", item_id: "", quantity: "", yield_percent: "100" });
  };

  const handleRemoveIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const calculateIngCost = (ing) => {
    const rawCost = ing.quantity * ing.price_per_unit;
    const yieldPct = ing.yield_percent || 100;
    return yieldPct > 0 ? rawCost / (yieldPct / 100) : rawCost;
  };

  const calculateTotalCost = () => {
    return formData.ingredients.reduce((sum, ing) => sum + calculateIngCost(ing), 0);
  };

  const handleSubmit = async () => {
    if (!formData.name || formData.ingredients.length === 0) {
      toast.error("Please enter a name and add at least one ingredient");
      return;
    }

    // Check oz_conversion for waste log items
    if (formData.allow_in_waste_log && !formData.oz_conversion) {
      toast.error("Please enter oz conversion for waste log tracking");
      return;
    }

    try {
      const payload = {
        name: formData.name,
        yield_quantity: parseFloat(formData.yield_quantity) || 1,
        yield_unit: formData.yield_unit,
        ingredients: formData.ingredients.map(i => ({
          item_type: i.item_type || "product",
          item_id: i.item_id,
          quantity: i.quantity,
          yield_percent: i.yield_percent || 100
        })),
        notes: formData.notes,
        track_in_inventory: formData.track_in_inventory,
        inventory_location_ids: formData.inventory_location_ids,
        inventory_storage_mappings: formData.inventory_storage_mappings,
        oz_conversion: formData.oz_conversion ? parseFloat(formData.oz_conversion) : null,
        pans_to_oz: formData.pans_to_oz ? parseFloat(formData.pans_to_oz) : null,
        allow_in_waste_log: formData.allow_in_waste_log
      };

      if (editingItem) {
        await axios.put(`${API}/prep-items/${editingItem.prep_id}`, payload, { withCredentials: true });
        toast.success("Prep item updated");
      } else {
        await axios.post(`${API}/prep-items`, payload, { withCredentials: true });
        toast.success("Prep item created");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save prep item");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      yield_quantity: item.yield_quantity?.toString() || "1",
      yield_unit: item.yield_unit || "EA",
      ingredients: (item.ingredients || []).map(ing => ({
        item_type: ing.item_type || "product",
        item_id: ing.item_id || ing.product_id || "",
        product_name: ing.product_name || "",
        quantity: ing.quantity || 0,
        price_per_unit: ing.price_per_unit || 0,
        yield_percent: ing.yield_percent ?? 100
      })),
      notes: item.notes || "",
      track_in_inventory: item.track_in_inventory || false,
      inventory_location_ids: item.inventory_location_ids || [],
      inventory_storage_mappings: item.inventory_storage_mappings || [],
      oz_conversion: item.oz_conversion?.toString() || "",
      pans_to_oz: item.pans_to_oz?.toString() || "",
      allow_in_waste_log: item.allow_in_waste_log || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (prepId) => {
    if (!window.confirm("Delete this prep item?")) return;
    try {
      await axios.delete(`${API}/prep-items/${prepId}`, { withCredentials: true });
      toast.success("Prep item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      yield_quantity: "1",
      yield_unit: "EA",
      ingredients: [],
      notes: "",
      track_in_inventory: false,
      inventory_location_ids: [],
      inventory_storage_mappings: [],
      oz_conversion: "",
      pans_to_oz: "",
      allow_in_waste_log: false
    });
    setNewIngredient({ item_type: "product", item_id: "", quantity: "", yield_percent: "100" });
  };

  // Filter prep items available as ingredients (exclude self when editing)
  const availablePrepIngredients = prepItems.filter(p => !editingItem || p.prep_id !== editingItem.prep_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="prep-calculator-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-wide font-semibold">
            PREP CALCULATOR
          </h1>
          <p className="text-muted-foreground mt-1">
            Calculate prepped item costs based on ingredients
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { 
          setDialogOpen(open); 
          if (!open) resetForm(); 
        }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-prep-btn">
              <Plus size={18} className="mr-2" />
              New Prep Item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-wide">
                {editingItem ? "EDIT PREP ITEM" : "NEW PREP ITEM"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label>Prep Item Name</Label>
                  <Input 
                    data-testid="prep-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., Poke Bowl Base"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label>Yield Quantity</Label>
                    <Input 
                      type="number"
                      value={formData.yield_quantity}
                      onChange={(e) => setFormData(p => ({ ...p, yield_quantity: e.target.value }))}
                      placeholder="1"
                      min="0.1"
                      step="0.1"
                    />
                  </div>
                  <div className="w-24">
                    <Label>Unit</Label>
                    <Select value={formData.yield_unit} onValueChange={(val) => setFormData(p => ({ ...p, yield_unit: val }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EA">EA</SelectItem>
                        <SelectItem value="LB">LB</SelectItem>
                        <SelectItem value="OZ">OZ</SelectItem>
                        <SelectItem value="GAL">GAL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Ingredients */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Ingredients</Label>
                
                {/* Add ingredient row */}
                <div className="space-y-2 p-3 border rounded-lg bg-muted/20">
                  <div className="flex gap-2 items-end">
                    <div className="w-28">
                      <Label className="text-xs">Type</Label>
                      <Select 
                        value={newIngredient.item_type} 
                        onValueChange={(val) => setNewIngredient(p => ({ ...p, item_type: val, item_id: "" }))}
                      >
                        <SelectTrigger data-testid="ingredient-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="product">Product</SelectItem>
                          <SelectItem value="prep">Prep Item</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">
                        {newIngredient.item_type === "prep" ? "Prep Item" : "Product"}
                      </Label>
                      <Select 
                        value={newIngredient.item_id} 
                        onValueChange={(val) => setNewIngredient(p => ({ ...p, item_id: val }))}
                      >
                        <SelectTrigger data-testid="ingredient-item-select">
                          <SelectValue placeholder="Select ingredient" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {newIngredient.item_type === "prep" ? (
                            availablePrepIngredients.length > 0 ? (
                              availablePrepIngredients.map(prep => (
                                <SelectItem key={prep.prep_id} value={prep.prep_id}>
                                  <span className="flex items-center gap-1">
                                    <CookingPot size={12} />
                                    {prep.name} (${prep.cost_per_unit?.toFixed(2)}/{prep.yield_unit})
                                  </span>
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="_none" disabled>No prep items available</SelectItem>
                            )
                          ) : (
                            products.map(prod => (
                              <SelectItem key={prod.product_id} value={prod.product_id}>
                                {prod.name} (${prod.price?.toFixed(2)}/{prod.unit || "CS"})
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="w-24">
                      <Label className="text-xs">Qty</Label>
                      <Input 
                        type="number"
                        value={newIngredient.quantity}
                        onChange={(e) => setNewIngredient(p => ({ ...p, quantity: e.target.value }))}
                        placeholder="Qty"
                        min="0"
                        step="0.01"
                        data-testid="ingredient-quantity-input"
                      />
                    </div>
                    <div className="w-24">
                      <Label className="text-xs">Yield %</Label>
                      <Input 
                        type="number"
                        value={newIngredient.yield_percent}
                        onChange={(e) => setNewIngredient(p => ({ ...p, yield_percent: e.target.value }))}
                        placeholder="100"
                        min="1"
                        max="100"
                        step="1"
                        data-testid="ingredient-yield-input"
                      />
                    </div>
                    <Button type="button" onClick={handleAddIngredient} size="icon" data-testid="add-ingredient-btn">
                      <Plus size={18} />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Yield % = usable portion (e.g., 80% means 20% waste, cost adjusts up)
                  </p>
                </div>

                {/* Ingredients list */}
                {formData.ingredients.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {formData.ingredients.map((ing, idx) => {
                      const rawCost = ing.quantity * ing.price_per_unit;
                      const adjustedCost = calculateIngCost(ing);
                      const showYield = (ing.yield_percent || 100) < 100;
                      
                      return (
                        <div key={ing.item_id || idx} className="flex items-center justify-between p-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {ing.item_type === "prep" && (
                                <CookingPot size={14} className="text-secondary shrink-0" />
                              )}
                              <p className="font-medium truncate">{ing.product_name}</p>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {ing.quantity} x ${ing.price_per_unit?.toFixed(2)}
                              {showYield && (
                                <span className="text-amber-600 ml-1">
                                  @ {ing.yield_percent}% yield
                                </span>
                              )}
                              {" = "}
                              <span className="font-medium">${adjustedCost.toFixed(2)}</span>
                              {showYield && (
                                <span className="text-xs text-muted-foreground ml-1">(raw: ${rawCost.toFixed(2)})</span>
                              )}
                            </p>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => handleRemoveIngredient(idx)} className="shrink-0">
                            <X size={16} className="text-destructive" />
                          </Button>
                        </div>
                      );
                    })}
                    <div className="p-3 bg-muted/50">
                      <div className="flex justify-between font-semibold">
                        <span>Total Cost</span>
                        <span className="text-primary">${calculateTotalCost().toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>Cost per {formData.yield_unit}</span>
                        <span>${(calculateTotalCost() / (parseFloat(formData.yield_quantity) || 1)).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label>Notes (Optional)</Label>
                <Input 
                  value={formData.notes}
                  onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g., Prep instructions"
                />
              </div>

              {/* Track in Inventory Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="track-inventory"
                    checked={formData.track_in_inventory}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      track_in_inventory: e.target.checked,
                      inventory_location_ids: e.target.checked ? p.inventory_location_ids : [],
                      inventory_storage_mappings: e.target.checked ? p.inventory_storage_mappings : []
                    }))}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="track-inventory" className="cursor-pointer font-medium">
                    Track in Inventory Count
                  </Label>
                </div>
                
                {formData.track_in_inventory && (
                  <div className="ml-7 space-y-2">
                    <Label className="text-sm text-muted-foreground">Select locations to track:</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {locations.map(loc => (
                        <label 
                          key={loc.location_id} 
                          className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-muted/50"
                        >
                          <input
                            type="checkbox"
                            checked={formData.inventory_location_ids.includes(loc.location_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData(p => ({
                                  ...p,
                                  inventory_location_ids: [...p.inventory_location_ids, loc.location_id]
                                }));
                              } else {
                                setFormData(p => ({
                                  ...p,
                                  inventory_location_ids: p.inventory_location_ids.filter(id => id !== loc.location_id),
                                  inventory_storage_mappings: p.inventory_storage_mappings.filter(m => m.location_id !== loc.location_id)
                                }));
                              }
                            }}
                            className="h-4 w-4 rounded"
                          />
                          <span className="text-sm">{loc.name}</span>
                        </label>
                      ))}
                    </div>
                    
                    {/* Storage Area Assignment per selected location */}
                    {formData.inventory_location_ids.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <Label className="text-sm text-muted-foreground">Assign to storage areas:</Label>
                        {formData.inventory_location_ids.map(locId => {
                          const loc = locations.find(l => l.location_id === locId);
                          const locAreas = storageAreas.filter(a => a.location_id === locId);
                          if (locAreas.length === 0) return null;
                          return (
                            <div key={locId} className="pl-2 border-l-2 border-primary/20">
                              <p className="text-xs font-medium text-muted-foreground mb-1">{loc?.name}</p>
                              <div className="flex flex-wrap gap-2">
                                {locAreas.map(area => {
                                  const isSelected = formData.inventory_storage_mappings.some(
                                    m => m.location_id === locId && m.storage_area_id === area.area_id
                                  );
                                  return (
                                    <label key={area.area_id} className="flex items-center gap-1 text-xs p-1.5 border rounded cursor-pointer hover:bg-muted/50">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setFormData(p => ({
                                              ...p,
                                              inventory_storage_mappings: [...p.inventory_storage_mappings, { location_id: locId, storage_area_id: area.area_id }]
                                            }));
                                          } else {
                                            setFormData(p => ({
                                              ...p,
                                              inventory_storage_mappings: p.inventory_storage_mappings.filter(
                                                m => !(m.location_id === locId && m.storage_area_id === area.area_id)
                                              )
                                            }));
                                          }
                                        }}
                                        className="h-3 w-3 rounded"
                                      />
                                      <span>{area.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Allow in Waste Log */}
                <div className="flex items-center gap-3 mt-4">
                  <input
                    type="checkbox"
                    id="allow-waste-log"
                    checked={formData.allow_in_waste_log}
                    onChange={(e) => setFormData(p => ({ 
                      ...p, 
                      allow_in_waste_log: e.target.checked
                    }))}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="allow-waste-log-checkbox"
                  />
                  <Label htmlFor="allow-waste-log" className="cursor-pointer font-medium">
                    Allow in Waste Log
                  </Label>
                </div>
                <p className="ml-7 text-xs text-muted-foreground mt-1">
                  Enable this to allow logging waste for this prep item
                </p>
                
                {/* Oz Conversion - Show when Allow in Waste Log is enabled */}
                {formData.allow_in_waste_log && (
                  <div className="mt-3">
                    <Label>
                      Oz Conversion 
                      <span className="text-xs text-orange-600 ml-2">* Required</span>
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input 
                        data-testid="prep-oz-conversion-input"
                        type="number"
                        value={formData.oz_conversion}
                        onChange={(e) => setFormData(p => ({ ...p, oz_conversion: e.target.value }))}
                        placeholder="Enter oz per pan"
                        min="0"
                        step="0.1"
                      />
                      <span className="text-sm text-muted-foreground whitespace-nowrap">oz per {formData.yield_unit || 'EA'}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      How many ounces in one {formData.yield_unit || 'pan'} of this prep item
                    </p>
                  </div>
                )}
                
                {/* Pans to Oz Conversion - For combined pan + oz waste tracking */}
                {formData.allow_in_waste_log && (
                  <div className="mt-3">
                    <Label>
                      Pans to Oz Conversion
                      <span className="text-xs text-muted-foreground ml-2">(Optional)</span>
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input 
                        data-testid="prep-pans-to-oz-input"
                        type="number"
                        value={formData.pans_to_oz}
                        onChange={(e) => setFormData(p => ({ ...p, pans_to_oz: e.target.value }))}
                        placeholder="e.g., 128"
                        min="0"
                        step="0.1"
                      />
                      <span className="text-sm text-muted-foreground whitespace-nowrap">oz per pan</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Allows waste to be logged in both pans AND oz, combined automatically
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="bg-primary" data-testid="save-prep-btn">
                <Check size={16} className="mr-2" />
                {editingItem ? "Update" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Prep Items List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {prepItems.length > 0 ? (
          prepItems.map(item => (
            <Card key={item.prep_id} className="border rounded-xl" data-testid={`prep-item-${item.prep_id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg tracking-wide">
                      {item.name}
                    </CardTitle>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.track_in_inventory && (
                        <Badge variant="secondary" className="text-xs">
                          Tracked in Inventory
                        </Badge>
                      )}
                      {item.allow_in_waste_log && (
                        <Badge variant="outline" className="text-xs border-red-300 text-red-600">
                          Waste Log
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(item)}>
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(item.prep_id)}>
                      <Trash size={14} className="text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Yield</span>
                    <Badge variant="outline">{item.yield_quantity} {item.yield_unit}</Badge>
                  </div>
                  
                  <div className="border-t pt-3">
                    <p className="text-xs text-muted-foreground mb-2">INGREDIENTS ({item.ingredients?.length || 0})</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {item.ingredients?.map((ing, idx) => (
                        <div key={ing.item_id || idx} className="flex justify-between text-sm gap-2">
                          <span className="truncate flex items-center gap-1">
                            {ing.item_type === "prep" && <CookingPot size={12} className="text-secondary shrink-0" />}
                            {ing.product_name}
                            {(ing.yield_percent || 100) < 100 && (
                              <span className="text-xs text-amber-600">({ing.yield_percent}%)</span>
                            )}
                          </span>
                          <span className="text-muted-foreground shrink-0">${ing.cost?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-3 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Total Cost</span>
                      <span className="font-semibold text-primary">${item.total_cost?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Cost per {item.yield_unit}</span>
                      <span className="font-semibold">${item.cost_per_unit?.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  {item.track_in_inventory && item.inventory_location_ids?.length > 0 && (
                    <div className="border-t pt-3">
                      <p className="text-xs text-muted-foreground mb-1">TRACKED AT:</p>
                      <div className="flex flex-wrap gap-1">
                        {item.inventory_location_ids.map(locId => {
                          const loc = locations.find(l => l.location_id === locId);
                          return loc ? (
                            <Badge key={locId} variant="outline" className="text-xs">
                              {loc.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="col-span-full border rounded-xl">
            <CardContent className="py-16 text-center">
              <Calculator size={48} weight="duotone" className="mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No prep items yet</p>
              <p className="text-sm text-muted-foreground mt-1">Create prep items to calculate ingredient costs</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
